import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import 'mana-font';
import { AuthProvider } from './hooks/useAuth';
import { useAuth } from './hooks/useAuth';
import { ToastProvider, ToastContainer } from './components/system/ToastContext';
import { ErrorBoundary } from './components/system/ErrorBoundary';
import { PendingOrdersProvider } from './hooks/usePendingOrders';
import { AdminLayout } from './components/layout/AdminLayout';
import { LoginPage } from './pages/index';
import { InventoryLookupPage } from './pages/InventoryLookupPage';
import { InventoryAddPage } from './pages/InventoryAddPage';
import { InventoryHistoryPage } from './pages/InventoryHistoryPage';
import { InventoryParsePage } from './pages/InventoryParsePage';
import PricingPage from './pages/PricingPage';
import ChannelsPage from './pages/ChannelsPage';
import ChannelsManaPoolPage from './pages/ChannelsManaPoolPage';
import ChannelsShopifyPage from './pages/ChannelsShopifyPage';
import ChannelsTcgplayerPage from './pages/ChannelsTcgplayerPage';
import OrdersPage from './pages/OrdersPage';
import OrdersManaPoolPage from './pages/OrdersManaPoolPage';
import OrdersShopifyPage from './pages/OrdersShopifyPage';
import CustomerBansPage from './pages/CustomerBansPage';
import { DashboardsPage } from './pages/DashboardsPage';
import { ChannelApprovalsPage } from './pages/ChannelApprovalsPage';
import { ReportsPullListPage } from './pages/ReportsPullListPage';
import { OrderPullPage } from './pages/OrderPullPage';
import { CkTradeinPage } from './pages/CkTradeinPage';
import { BuylistQueuePage } from './pages/BuylistQueuePage';
import { BuylistSubmissionPage } from './pages/BuylistSubmissionPage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportsReconciliationPage } from './pages/ReportsReconciliationPage';
import { ReportsSyncReliabilityPage } from './pages/ReportsSyncReliabilityPage';
import { ReportsOutboxReliabilityPage } from './pages/ReportsOutboxReliabilityPage';
import { ReportsFanoutSloBreachesPage } from './pages/ReportsFanoutSloBreachesPage';
import { ReportsChannelParityPage } from './pages/ReportsChannelParityPage';
import { ReportsListingCoveragePage } from './pages/ReportsListingCoveragePage';
import { ReportsShippedShortfallPage } from './pages/ReportsShippedShortfallPage';
import { ReportsFulfillmentAgingPage } from './pages/ReportsFulfillmentAgingPage';
import { ReportsWeeklyHoursPage } from './pages/ReportsWeeklyHoursPage';
import { ReportsSalesCogsPage } from './pages/ReportsSalesCogsPage';
import { ReportsSalesOverviewPage } from './pages/ReportsSalesOverviewPage';
import { ReportsShrinkPage } from './pages/ReportsShrinkPage';
import { ReportsInventoryValuePage } from './pages/ReportsInventoryValuePage';
import { SealedLookupPage } from './pages/SealedLookupPage';
import { TimeClockPage } from './pages/TimeClockPage';
import { EmployeeManagementPage } from './pages/EmployeeManagementPage';
import { getAppEnv } from './config/runtimeConfig';
import { inventoryLookupPath, isInventoryLookupGame, lookupGameForProductLine, type InventoryLookupGame } from './lib/inventoryNav';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const InventoryLookupRedirect: React.FC = () => {
  const [params] = useSearchParams();
  const game: InventoryLookupGame = lookupGameForProductLine(params.get('product_line'))
    ?? lookupGameForProductLine(params.get('game'))
    ?? 'mtg';
  const next = new URLSearchParams(params);
  next.delete('product_line');
  next.delete('game');
  const search = next.toString();
  return (
    <Navigate
      to={{ pathname: inventoryLookupPath(game), search: search ? `?${search}` : '' }}
      replace
    />
  );
};

const SealedReportRedirect: React.FC = () => {
  const [params] = useSearchParams();
  const next = new URLSearchParams();
  const query = (params.get('q') || params.get('search') || '').trim();
  if (query) next.set('q', query);
  const search = next.toString();
  return (
    <Navigate
      to={{ pathname: '/inventory/sealed', search: search ? `?${search}` : '' }}
      replace
    />
  );
};

const InventoryLookupRoute: React.FC = () => {
  const { game } = useParams<{ game: string }>();
  if (!isInventoryLookupGame(game)) {
    return <InventoryLookupRedirect />;
  }
  return <InventoryLookupPage />;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/inventory" replace />} />
      <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route path="/time/clock" element={<TimeClockPage />} />
        <Route path="/time/employees" element={<EmployeeManagementPage />} />
        <Route path="/inventory" element={<InventoryLookupRedirect />} />
        <Route path="/inventory/lookup" element={<InventoryLookupRedirect />} />
        <Route path="/inventory/sealed" element={<SealedLookupPage />} />
        <Route path="/inventory/add" element={<InventoryAddPage />} />
        <Route path="/inventory/history" element={<InventoryHistoryPage />} />
        <Route path="/inventory/parse" element={<InventoryParsePage />} />
        <Route path="/inventory/:game" element={<InventoryLookupRoute />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/channels" element={<Navigate to="/channels/combined" replace />} />
        <Route path="/channels/combined" element={<ChannelsPage />} />
        <Route path="/channels/manapool" element={<ChannelsManaPoolPage />} />
        <Route path="/channels/shopify" element={<ChannelsShopifyPage />} />
        <Route path="/channels/tcgplayer" element={<ChannelsTcgplayerPage />} />
        <Route path="/orders" element={<Navigate to="/orders/combined" replace />} />
        <Route path="/orders/combined" element={<OrdersPage />} />
        <Route path="/orders/manapool" element={<OrdersManaPoolPage />} />
        <Route path="/orders/shopify" element={<OrdersShopifyPage />} />
        <Route path="/orders/bans" element={<CustomerBansPage />} />
        <Route path="/orders/order-pull" element={<OrderPullPage />} />
        <Route path="/orders/order-pull/:trayId" element={<OrderPullPage />} />
        <Route path="/dashboards" element={<DashboardsPage />} />
        <Route path="/channels/approvals" element={<ChannelApprovalsPage />} />
        <Route path="/reports" element={<Navigate to="/reports/channel-parity" replace />} />
        <Route path="/reports/reconciliation" element={<ReportsReconciliationPage />} />
        <Route path="/reports/sync-reliability" element={<ReportsSyncReliabilityPage />} />
        <Route path="/reports/outbox-reliability" element={<ReportsOutboxReliabilityPage />} />
        <Route path="/reports/fanout-slo-breaches" element={<ReportsFanoutSloBreachesPage />} />
        <Route path="/reports/channel-parity" element={<ReportsChannelParityPage />} />
        <Route path="/reports/listing-coverage" element={<ReportsListingCoveragePage />} />
        <Route path="/reports/shipped-shortfall-with-stock" element={<ReportsShippedShortfallPage />} />
        <Route path="/reports/fulfillment-aging" element={<ReportsFulfillmentAgingPage />} />
        <Route path="/reports/weekly-hours" element={<ReportsWeeklyHoursPage />} />
        <Route path="/reports/sales-overview" element={<ReportsSalesOverviewPage />} />
        <Route path="/reports/sales-cogs" element={<ReportsSalesCogsPage />} />
        <Route path="/reports/shrink" element={<ReportsShrinkPage />} />
        <Route path="/reports/inventory-value" element={<ReportsInventoryValuePage />} />
        <Route path="/reports/sealed-valuation" element={<SealedReportRedirect />} />
        <Route path="/reports/pull-list" element={<ReportsPullListPage />} />
        <Route path="/reports/ck-tradein" element={<Navigate to="/ck-tradein" replace />} />
        <Route path="/buylist" element={<BuylistQueuePage />} />
        <Route path="/buylist/:id" element={<BuylistSubmissionPage />} />
        <Route path="/ck-tradein" element={<CkTradeinPage />} />
        <Route path="/ck-tradein/:batchId" element={<CkTradeinPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    const env = getAppEnv();
    const prefix = env === 'prod' ? '' : `[${env.toUpperCase()}] `;
    document.title = `${prefix}TCG SyncBase`;
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <PendingOrdersProvider>
            <Router>
              <AppContent />
              <ToastContainer />
            </Router>
          </PendingOrdersProvider>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
